
import { Patient } from '../types';
import { supabase } from '@/integrations/supabase/client';

// Helper function to get mock data from localStorage
const getMockPatients = (): Patient[] => {
  const storedData = localStorage.getItem('mock_patients');
  return storedData ? JSON.parse(storedData) : [];
};

// Helper function to save mock data to localStorage
const saveMockPatients = (patients: Patient[]): void => {
  localStorage.setItem('mock_patients', JSON.stringify(patients));
};

// Helper function to reset mock data
const resetMockPatients = (): void => {
  localStorage.removeItem('mock_patients');
};

// Check if mock data is allowed
const useMockData = (): boolean => {
  return import.meta.env.VITE_USE_MOCK_DATA === 'true';
};

// Map Supabase patient data to our app's Patient type
const mapSupabasePatient = (patient: any): Patient => {
  return {
    id: patient.id,
    fullName: patient.full_name,
    email: patient.email || '',
    phone: patient.phone,
    address: patient.address || '',
    city: patient.city || '',
    state: patient.state || '',
    country: patient.country || '',
    zipCode: patient.zip_code || '',
    notes: patient.notes || '',
    gender: patient.gender || '',
    age: patient.age || null,
    createdAt: patient.created_at
  };
};

export const patientService = {
  // Reset all mock patient data
  resetAll: async (): Promise<void> => {
    try {
      // Clear Supabase patients table
      const { error } = await supabase
        .from('patients')
        .delete()
        .not('id', 'is', null);
        
      if (error) throw error;
      
      // Also reset local storage
      resetMockPatients();
    } catch (error: any) {
      // In development, just clear localStorage
      if ((import.meta.env.DEV || import.meta.env.MODE === 'development')) {
        resetMockPatients();
      } else {
        console.error('Error resetting patients data:', error);
        throw error;
      }
    }
  },

  // Get all patients
  getAll: async (): Promise<Patient[]> => {
    try {
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return data.map(mapSupabasePatient);
    } catch (error: any) {
      // Handle network errors in development with mock data
      if ((import.meta.env.DEV || import.meta.env.MODE === 'development') && useMockData()) {
        console.warn('Using mock patients data due to error. In production, this would fail.');
        
        // Get mock data from localStorage or use empty array if none exists
        return getMockPatients();
      }
      console.error('Error fetching patients:', error);
      throw error;
    }
  },
  
  // Get patient by ID
  getById: async (id: string): Promise<Patient> => {
    try {
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      
      return mapSupabasePatient(data);
    } catch (error: any) {
      // Handle network errors in development with mock data
      if ((import.meta.env.DEV || import.meta.env.MODE === 'development') && useMockData()) {
        console.warn('Using mock patient data due to error. In production, this would fail.');
        
        const patients = getMockPatients();
        const patient = patients.find(p => p.id === id);
        
        if (patient) {
          return patient;
        } else {
          throw new Error('Patient not found');
        }
      }
      console.error('Error fetching patient:', error);
      throw error;
    }
  },
  
  // Create new patient
  create: async (patientData: Omit<Patient, 'id' | 'createdAt'>): Promise<Patient> => {
    try {
      // Convert to Supabase format
      const supabasePatient = {
        full_name: patientData.fullName,
        email: patientData.email,
        phone: patientData.phone,
        address: patientData.address,
        city: patientData.city,
        state: patientData.state,
        country: patientData.country,
        zip_code: patientData.zipCode,
        notes: patientData.notes,
        gender: patientData.gender,
        age: patientData.age,
        user_id: null // To be filled if linked to a user account
      };
      
      const { data, error } = await supabase
        .from('patients')
        .insert(supabasePatient)
        .select()
        .single();
      
      if (error) throw error;
      
      return mapSupabasePatient(data);
    } catch (error: any) {
      // Handle network errors in development with mock data
      if ((import.meta.env.DEV || import.meta.env.MODE === 'development') && useMockData()) {
        console.warn('Using mock create patient response due to error. In production, this would fail.');
        
        const patients = getMockPatients();
        const newId = Math.random().toString(36).substring(2, 10);
        
        const newPatient: Patient = {
          id: newId,
          ...patientData,
          createdAt: new Date().toISOString()
        };
        
        patients.push(newPatient);
        saveMockPatients(patients);
        
        return newPatient;
      }
      console.error('Error creating patient:', error);
      throw error;
    }
  },
  
  // Update patient
  update: async (id: string, patientData: Partial<Patient>): Promise<Patient> => {
    try {
      // Convert to Supabase format
      const supabasePatient: any = {};
      
      if (patientData.fullName !== undefined) supabasePatient.full_name = patientData.fullName;
      if (patientData.email !== undefined) supabasePatient.email = patientData.email;
      if (patientData.phone !== undefined) supabasePatient.phone = patientData.phone;
      if (patientData.address !== undefined) supabasePatient.address = patientData.address;
      if (patientData.city !== undefined) supabasePatient.city = patientData.city;
      if (patientData.state !== undefined) supabasePatient.state = patientData.state;
      if (patientData.country !== undefined) supabasePatient.country = patientData.country;
      if (patientData.zipCode !== undefined) supabasePatient.zip_code = patientData.zipCode;
      if (patientData.notes !== undefined) supabasePatient.notes = patientData.notes;
      if (patientData.gender !== undefined) supabasePatient.gender = patientData.gender;
      if (patientData.age !== undefined) supabasePatient.age = patientData.age;
      
      const { data, error } = await supabase
        .from('patients')
        .update(supabasePatient)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      return mapSupabasePatient(data);
    } catch (error: any) {
      // Handle network errors in development with mock data
      if ((import.meta.env.DEV || import.meta.env.MODE === 'development') && useMockData()) {
        console.warn('Using mock update patient response due to error. In production, this would fail.');
        
        const patients = getMockPatients();
        const index = patients.findIndex(p => p.id === id);
        
        if (index === -1) {
          throw new Error('Patient not found');
        }
        
        patients[index] = {
          ...patients[index],
          ...patientData
        };
        
        saveMockPatients(patients);
        
        return patients[index];
      }
      console.error('Error updating patient:', error);
      throw error;
    }
  },
  
  // Delete patient
  delete: async (id: string): Promise<void> => {
    try {
      console.log(`Attempting to delete patient with ID: ${id}`);
      
      // Check for appointments associated with this patient first
      const { data: appointments, error: appointmentError } = await supabase
        .from('appointments')
        .select('id')
        .eq('patient_id', id);
        
      if (appointmentError) {
        console.error('Error checking for patient appointments:', appointmentError);
      }
      
      if (appointments && appointments.length > 0) {
        console.log(`Found ${appointments.length} appointments for patient ${id}`);
        console.log('Patient will be deleted, but appointments will be preserved');
      }
      
      // Now delete the patient
      const { error } = await supabase
        .from('patients')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      console.log(`Successfully deleted patient with ID: ${id}`);
    } catch (error: any) {
      // Handle network errors in development with mock data
      if ((import.meta.env.DEV || import.meta.env.MODE === 'development') && useMockData()) {
        console.warn('Using mock delete patient response due to error. In production, this would fail.');
        
        const patients = getMockPatients();
        const filteredPatients = patients.filter(p => p.id !== id);
        
        if (patients.length === filteredPatients.length) {
          throw new Error('Patient not found');
        }
        
        saveMockPatients(filteredPatients);
        return;
      }
      console.error('Error deleting patient:', error);
      throw error;
    }
  }
};
